import { useCallback, useEffect, useRef, useState } from "react";
import { extractErrorShape, logAuthEvent, newAuthEventId } from "@/lib/authLogging";
import { broadcast, subscribe } from "@/utils/common/crossTabChannel";
import { clearCachesAndLogout, isLoggingOut } from "@/utils/common/logout";

interface SessionConfig {
  idleTimeoutMinutes: number;
  sessionExpiresAt: number | null;
  tokenExpiresAt: number | null;
  claims?: any;
}

interface SessionTimeoutState {
  /** Whether session config has been loaded */
  isLoaded: boolean;
  /** Idle timeout in minutes (0 = indefinite) */
  idleTimeoutMinutes: number;
  /** Unix timestamp when absolute session expires (null = indefinite) */
  sessionExpiresAt: number | null;
  /** Whether the idle warning should be shown */
  showIdleWarning: boolean;
  /** Whether the absolute session timeout warning should be shown */
  showAbsoluteWarning: boolean;
  /** Seconds remaining until idle timeout (only set when warning is shown) */
  idleSecondsRemaining: number;
  /** Seconds remaining until absolute session expires */
  absoluteSecondsRemaining: number | null;
  /** Reset the idle timer (call on user activity) */
  resetIdleTimer: () => void;
  /**
   * Refresh the token and reset the idle timer.
   * Note: This does NOT extend the absolute session timeout (session_expires_at).
   * The absolute timeout is set once at login and preserved in refreshed tokens
   * per OWASP guidelines - it represents a hard cap on session duration.
   */
  refreshTokenAndResetIdle: () => Promise<void>;
  /** Full decoded claims (debug only) */
  claims?: any;
}

interface UseSessionTimeoutOptions {
  /** Whether the hook is enabled. Set to false when user is not authenticated. */
  enabled?: boolean;
  /** Seconds before idle timeout to show warning (default: 120 = 2 minutes) */
  warningBeforeIdleTimeout?: number;
  /** Seconds before absolute timeout to show warning (default: 300 = 5 minutes) */
  warningBeforeAbsoluteTimeout?: number;
  /** Callback when user should be logged out */
  onLogout?: () => void;
}

const DEFAULT_WARNING_BEFORE_IDLE = 120; // 2 minutes before idle timeout
const DEFAULT_WARNING_BEFORE_ABSOLUTE = 300; // 5 minutes before absolute timeout
const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "touchstart",
  "scroll",
  "click",
] as const;

// Throttle activity detection to avoid excessive updates
const ACTIVITY_THROTTLE_MS = 1000;

// Cross-tab channel name used to broadcast activity so a backgrounded tab —
// which never sees mouse/key events — doesn't tick down to the idle-timeout.
const CROSS_TAB_ACTIVITY_CHANNEL = "lastActivityAt";

// The local idle-timer reset throttles at 1s for sub-second responsiveness.
// Cross-tab broadcasts only need to fire often enough that sibling tabs don't
// hit the idle timeout (measured in minutes), so 10s is plenty — and it caps
// the fan-out cost at O(N tabs)/10s instead of O(N tabs)/1s.
const CROSS_TAB_BROADCAST_THROTTLE_MS = 10_000;

// One-shot per page-load: if localStorage writes fail (private mode, quota
// exceeded, restrictive extensions), cross-tab sync silently degrades — the
// user looks like the pre-fix mystery-logout pattern. Surface the failure
// once via the auth-event stream so support can correlate reports with
// "this user's localStorage was broken."
let crossTabWriteFailureLogged = false;
function logCrossTabWriteFailureOnce(error: unknown): void {
  if (crossTabWriteFailureLogged) return;
  crossTabWriteFailureLogged = true;
  logAuthEvent({
    event: "session_timeout.cross_tab_write_failed",
    auth_event_id: newAuthEventId(),
    error: extractErrorShape(error),
  });
}

// Re-export for testing compatibility
export { _resetLogoutGuard } from "@/utils/common/logout";

/** @internal Test-only hook to reset the once-per-page-load dedup flag. */
export function _resetCrossTabWriteFailureGuard(): void {
  crossTabWriteFailureLogged = false;
}

/**
 * Hook for managing session timeouts with both idle (inactivity) and absolute timeout support.
 *
 * Per OWASP guidelines:
 * - Idle timeout: Client-side tracking of user inactivity
 * - Absolute timeout: Server-side enforcement via session_expires_at claim
 *
 * This hook:
 * 1. Fetches session config from the server (idle_timeout_minutes, session_expires_at)
 * 2. Tracks user activity to detect inactivity
 * 3. Shows warning before idle timeout
 * 4. Triggers logout when either timeout is reached
 */
export function useSessionTimeout(options: UseSessionTimeoutOptions = {}): SessionTimeoutState {
  const {
    enabled = true,
    warningBeforeIdleTimeout = DEFAULT_WARNING_BEFORE_IDLE,
    warningBeforeAbsoluteTimeout = DEFAULT_WARNING_BEFORE_ABSOLUTE,
    onLogout,
  } = options;

  // State
  const [isLoaded, setIsLoaded] = useState(false);
  const [idleTimeoutMinutes, setIdleTimeoutMinutes] = useState(0);
  const [sessionExpiresAt, setSessionExpiresAt] = useState<number | null>(null);
  const [claims, setClaims] = useState<any>(undefined);
  const [showIdleWarning, setShowIdleWarning] = useState(false);
  const [showAbsoluteWarning, setShowAbsoluteWarning] = useState(false);
  const [idleSecondsRemaining, setIdleSecondsRemaining] = useState(0);
  const [absoluteSecondsRemaining, setAbsoluteSecondsRemaining] = useState<number | null>(null);

  // Refs for tracking
  const lastActivityRef = useRef<number>(Date.now());
  const idleTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const absoluteTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isMountedRef = useRef(true);
  const lastActivityEventRef = useRef<number>(0);
  const lastCrossTabBroadcastRef = useRef<number>(0);
  const onLogoutRef = useRef(onLogout);
  onLogoutRef.current = onLogout;

  /**
   * Fetch session config from server
   */
  const fetchSessionConfig = useCallback(async (): Promise<SessionConfig | null> => {
    try {
      const response = await fetch("/api/auth/session-config", {
        method: "GET",
        credentials: "include",
      });

      if (!response.ok) {
        if (response.status === 401) {
          return null; // Not authenticated
        }
        throw new Error(`Failed to fetch session config: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching session config:", error);
      return null;
    }
  }, []);

  /**
   * Handle logout
   */
  const handleLogout = useCallback(async () => {
    if (isLoggingOut()) return;

    if (onLogoutRef.current) {
      onLogoutRef.current();
    } else {
      await clearCachesAndLogout();
    }
  }, []);

  /**
   * Reset idle timer (called on user activity).
   *
   * setShowIdleWarning(false) is called unconditionally — React no-ops a
   * same-value setState, so the prior `if (showIdleWarning)` gate added a
   * dep without changing behavior. Dropping it keeps this callback's
   * identity stable across warning flips, which in turn keeps the
   * activity-listener effect from tearing down and rebinding.
   */
  const resetIdleTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowIdleWarning(false);
  }, []);

  /**
   * Stay Logged In resets idle tracking. Access-token rotation lives on
   * `/api/auth/refresh` (see SessionTimeoutProvider + tryRefreshToken).
   */
  const refreshTokenAndResetIdle = useCallback(async () => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  /**
   * Track user activity
   */
  useEffect(() => {
    if (!enabled || idleTimeoutMinutes === 0) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityEventRef.current < ACTIVITY_THROTTLE_MS) {
        return;
      }
      lastActivityEventRef.current = now;
      resetIdleTimer();

      // Cross-tab broadcast throttle is independent of (and longer than) the
      // local activity throttle — see CROSS_TAB_BROADCAST_THROTTLE_MS.
      if (now - lastCrossTabBroadcastRef.current < CROSS_TAB_BROADCAST_THROTTLE_MS) {
        return;
      }
      lastCrossTabBroadcastRef.current = now;
      broadcast(CROSS_TAB_ACTIVITY_CHANNEL, String(now), logCrossTabWriteFailureOnce);
    };

    const unsubscribeCrossTab = subscribe(CROSS_TAB_ACTIVITY_CHANNEL, (raw) => {
      const timestamp = Number(raw);
      if (!Number.isFinite(timestamp)) return;
      // Treat cross-tab activity as our own activity. Use Math.max so a
      // delayed broadcast can't move the ref backwards past newer local
      // activity. setShowIdleWarning(false) is unconditional — React no-ops
      // a same-value setState — which keeps this effect from re-binding
      // listeners on every warning flip.
      lastActivityRef.current = Math.max(lastActivityRef.current, timestamp);
      setShowIdleWarning(false);
    });

    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      unsubscribeCrossTab();
    };
  }, [enabled, idleTimeoutMinutes, resetIdleTimer]);

  /**
   * Idle timeout checker
   */
  useEffect(() => {
    if (!enabled || !isLoaded || idleTimeoutMinutes === 0) {
      if (idleTimerRef.current) {
        clearInterval(idleTimerRef.current);
        idleTimerRef.current = null;
      }
      return;
    }

    const idleTimeoutMs = idleTimeoutMinutes * 60 * 1000;
    const warningTimeMs = warningBeforeIdleTimeout * 1000;

    idleTimerRef.current = setInterval(() => {
      if (!isMountedRef.current) return;

      const now = Date.now();
      const idleMs = now - lastActivityRef.current;
      const remainingMs = idleTimeoutMs - idleMs;
      const remainingSeconds = Math.max(0, Math.floor(remainingMs / 1000));

      // Check if we should show warning
      if (remainingMs <= warningTimeMs && remainingMs > 0) {
        setShowIdleWarning(true);
      } else if (remainingMs > warningTimeMs) {
        setShowIdleWarning(false);
      }

      // Update seconds remaining (always, for debug info)
      setIdleSecondsRemaining(remainingSeconds);

      // Check if idle timeout reached
      if (remainingMs <= 0) {
        handleLogout();
      }
    }, 1000);

    return () => {
      if (idleTimerRef.current) {
        clearInterval(idleTimerRef.current);
        idleTimerRef.current = null;
      }
    };
  }, [enabled, isLoaded, idleTimeoutMinutes, warningBeforeIdleTimeout, handleLogout]);

  /**
   * Absolute timeout checker
   */
  useEffect(() => {
    if (!enabled || !isLoaded || sessionExpiresAt === null) {
      if (absoluteTimerRef.current) {
        clearInterval(absoluteTimerRef.current);
        absoluteTimerRef.current = null;
      }
      setAbsoluteSecondsRemaining(null);
      setShowAbsoluteWarning(false);
      return;
    }

    absoluteTimerRef.current = setInterval(() => {
      if (!isMountedRef.current) return;

      const now = Math.floor(Date.now() / 1000);
      const remaining = sessionExpiresAt - now;

      setAbsoluteSecondsRemaining(Math.max(0, remaining));

      // Check if we should show warning
      if (remaining <= warningBeforeAbsoluteTimeout && remaining > 0) {
        setShowAbsoluteWarning(true);
      } else {
        // Clear warning when above threshold or when expired (remaining <= 0)
        setShowAbsoluteWarning(false);
      }

      // Check if absolute timeout reached
      if (remaining <= 0) {
        handleLogout();
      }
    }, 1000);

    return () => {
      if (absoluteTimerRef.current) {
        clearInterval(absoluteTimerRef.current);
        absoluteTimerRef.current = null;
      }
    };
  }, [enabled, isLoaded, sessionExpiresAt, warningBeforeAbsoluteTimeout, handleLogout]);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    isMountedRef.current = true;

    if (!enabled) {
      setIsLoaded(true);
      return;
    }

    fetchSessionConfig().then((config) => {
      if (config && isMountedRef.current) {
        setIdleTimeoutMinutes(config.idleTimeoutMinutes);
        setSessionExpiresAt(config.sessionExpiresAt);
        setClaims(config.claims);
        lastActivityRef.current = Date.now();
      }
      if (isMountedRef.current) {
        setIsLoaded(true);
      }
    });

    return () => {
      isMountedRef.current = false;
    };
  }, [enabled, fetchSessionConfig]);

  /**
   * Re-read session config when the tab becomes visible.
   * The proxy auto-refreshes tokens on every /api request, so the session
   * cookie will have fresh claims. Re-reading on visibility change picks up
   * any config changes (e.g. admin changed idle_timeout_minutes) without
   * needing a custom event system.
   */
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        fetchSessionConfig().then((config) => {
          if (config && isMountedRef.current) {
            setIdleTimeoutMinutes(config.idleTimeoutMinutes);
            setSessionExpiresAt(config.sessionExpiresAt);
            setClaims(config.claims);
          }
        });
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, fetchSessionConfig]);

  return {
    isLoaded,
    idleTimeoutMinutes,
    sessionExpiresAt,
    claims,
    showIdleWarning,
    showAbsoluteWarning,
    idleSecondsRemaining,
    absoluteSecondsRemaining,
    resetIdleTimer,
    refreshTokenAndResetIdle,
  };
}
